import type { VariableWrite } from '../../lib/api';
import type { AskInput, VariableDef, VariableSource, VariableType } from '../../lib/types';

/**
 * One "what kind of question" picker instead of exposing the storage split
 * (type=checkbox vs askInput): yesno maps to a checkbox variable, everything
 * else to a text variable with the matching askInput.
 */
export type QuestionKind = 'yesno' | AskInput;

export interface VariableFormState {
  key: string;
  label: string;
  /** The merge type picked when the source is `auto`. */
  type: VariableType;
  required: boolean;
  defaultValue: string;
  /** A checkbox's default is a state, not a string — kept separate so switching type back and forth doesn't destroy whichever one you already typed. */
  defaultTicked: boolean;
  source: VariableSource;
  prompt: string;
  helpText: string;
  qKind: QuestionKind;
  optionsText: string;
  groupKey: string;
  askWhenKey: string;
  askWhenValue: string;
}

export type VariableFormAction =
  | { type: 'patch'; patch: Partial<VariableFormState> }
  | { type: 'setSource'; source: VariableSource }
  | { type: 'setAskWhenKey'; key: string; gateType: VariableType | undefined };

export function initialFormState(initial: VariableDef | null): VariableFormState {
  return {
    key: initial?.key ?? '',
    label: initial?.label ?? '',
    type: initial?.type ?? 'text',
    required: initial?.required ?? false,
    defaultValue: initial?.default ?? '',
    defaultTicked: initial?.default === 'true',
    source: initial?.source ?? 'auto',
    prompt: initial?.prompt ?? '',
    helpText: initial?.helpText ?? '',
    qKind: initial
      ? initial.type === 'checkbox'
        ? 'yesno'
        : initial.askInput && initial.askInput !== 'text'
          ? initial.askInput
          : 'text'
      : 'text',
    optionsText: (initial?.options ?? []).join('\n'),
    groupKey: initial?.groupKey ?? '',
    askWhenKey: initial?.askWhenKey ?? '',
    askWhenValue: initial?.askWhenValue ?? '',
  };
}

/**
 * All the cross-field reconciliation lives here: flipping the source keeps the
 * two type pickers agreeing (so it never silently changes how the field
 * merges), and picking a gate variable seeds a sensible comparison value.
 */
export function variableFormReducer(
  state: VariableFormState,
  action: VariableFormAction,
): VariableFormState {
  switch (action.type) {
    case 'patch':
      return { ...state, ...action.patch };
    case 'setSource':
      if (action.source === 'asked') {
        return {
          ...state,
          source: 'asked',
          qKind: state.type === 'checkbox' ? 'yesno' : state.qKind === 'yesno' ? 'text' : state.qKind,
        };
      }
      return { ...state, source: 'auto', type: state.qKind === 'yesno' ? 'checkbox' : 'text' };
    case 'setAskWhenKey':
      return {
        ...state,
        askWhenKey: action.key,
        askWhenValue: action.gateType === 'checkbox' ? 'true' : '',
      };
  }
}

export interface DerivedForm {
  asked: boolean;
  /** When asked, the question kind DECIDES the merge type; when auto, the Type select does. */
  effType: VariableType;
  isCheckbox: boolean;
  grouped: boolean;
  withOptions: boolean;
  sentDefault: string;
  renaming: boolean;
  retyping: boolean;
}

export function deriveForm(s: VariableFormState, initial: VariableDef | null): DerivedForm {
  const asked = s.source === 'asked';
  const effType: VariableType = asked ? (s.qKind === 'yesno' ? 'checkbox' : 'text') : s.type;
  const isCheckbox = effType === 'checkbox';
  const placed = (initial?.usage?.placements ?? 0) > 0;
  return {
    asked,
    effType,
    isCheckbox,
    grouped: asked && isCheckbox && !!s.groupKey,
    withOptions: asked && (s.qKind === 'choice' || s.qKind === 'multichoice'),
    sentDefault: isCheckbox ? (s.defaultTicked ? 'true' : '') : s.defaultValue,
    renaming: !!initial && s.key !== initial.key && placed,
    retyping: !!initial && effType !== initial.type && placed,
  };
}

/** The request body for create (default omitted when blank) or update (default sent to clear). */
export function toVariableWrite(
  s: VariableFormState,
  initial: VariableDef | null,
): VariableWrite & { key: string; label: string } {
  const d = deriveForm(s, initial);
  return {
    key: s.key,
    label: s.label,
    type: d.effType,
    required: d.grouped ? false : s.required,
    default: initial ? d.sentDefault : d.sentDefault || undefined,
    source: s.source,
    prompt: d.asked && !d.grouped ? s.prompt.trim() || null : null,
    helpText: d.asked && !d.grouped ? s.helpText.trim() || null : null,
    askInput: d.asked && !d.isCheckbox && s.qKind !== 'text' ? (s.qKind as AskInput) : null,
    options: d.withOptions
      ? s.optionsText
          .split('\n')
          .map((o) => o.trim())
          .filter(Boolean)
      : null,
    groupKey: d.grouped ? s.groupKey : null,
    askWhenKey: d.asked && !d.grouped && s.askWhenKey ? s.askWhenKey : null,
    askWhenValue: d.asked && !d.grouped && s.askWhenKey ? s.askWhenValue.trim() || null : null,
  };
}
